import { inject, type Provider } from '@angular/core';
import { catchError, map, shareReplay, throwError, type Observable } from 'rxjs';
import {
  coincide,
  fuentePantallas,
  proveerFuenteIrA,
  type IrAResultado,
} from '@intelishs-group/ui/ir-a';
import type { CatalogoResumen } from '@schemas/catalogos';
import type { Cuenta } from '@schemas/cuentas';
import { CuentasService } from './cuentas.service';
import { CatalogosService } from './catalogos.service';
import { NavegacionService } from './navegacion.service';

/**
 * Las fuentes de «Ir a…» (Ctrl+K) de este portal. Van en `app.config.ts`, en la
 * raíz: el buscador lo monta el shell, por encima del `<router-outlet>`, y una
 * fuente provista en una ruta no le llega.
 *
 * ── Pantallas: las PROPIAS, no las del shell ───────────────────────────────
 *
 * El shell arma su fuente de Pantallas con los `modulos` del riel, y aquí el riel
 * trae el grupo «Acceso» y no sus hijas: Ctrl+K no encontraba Cuentas, Roles ni
 * Aplicaciones. Ésta lleva el mismo `tipo` y la reemplaza en su lugar
 * (`@intelishs-group/ui` ≥ 5.3.1), con las hijas del hub y «Acceso» de contexto.
 * Se lee en cada búsqueda: sigue a la sesión igual que el riel.
 *
 * ── Catálogos ────────────────────────────────────────────────────────────────
 *
 * Los sueltos y las categorías del catálogo general (cerca de cien): cada uno se
 * abre en `/catalogos/{clave}`, y recordar cómo se llama es justo lo que un
 * buscador resuelve. La lista se pide UNA vez y se busca en memoria: el listado
 * trae los conteos de tres bases y no cambia mientras se usa. Si falla no se
 * guarda el error: la siguiente búsqueda la vuelve a pedir.
 *
 * ── Cuentas ──────────────────────────────────────────────────────────────────
 *
 * El listado del servidor, que ya busca por login, nombre completo (sin acentos)
 * y correo. Una cuenta no tiene ruta propia —se edita en un diálogo—, así que el
 * resultado lleva al listado filtrado por su login (`?buscar=`): su fila, con
 * sus acciones, sin abrir de golpe un formulario que escribe. «Ver todos» lleva
 * al mismo listado con el texto que se escribió.
 */
export function proveerFuentesIrA(): Provider[] {
  return [
    proveerFuenteIrA(() => {
      const navegacion = inject(NavegacionService);
      return fuentePantallas(() => navegacion.pantallas());
    }),
    proveerFuenteIrA(() => {
      const navegacion = inject(NavegacionService);
      const catalogos = inject(CatalogosService);
      let lista$: Observable<readonly CatalogoResumen[]> | null = null;
      const lista = () =>
        (lista$ ??= catalogos.listar().pipe(
          catchError((e: unknown) => {
            lista$ = null;
            return throwError(() => e);
          }),
          shareReplay(1),
        ));
      return {
        tipo: 'catalogo',
        titulo: 'Catálogos',
        icono: 'database',
        activa: () => navegacion.puedeVer('catalogos'),
        buscar: (_texto, palabras) =>
          lista().pipe(
            map((l) =>
              l
                .filter((c) => coincide(palabras, c.plural, c.singular, c.tabla))
                .map((c) => aResultadoCatalogo(c)),
            ),
          ),
      };
    }),
    proveerFuenteIrA(() => {
      const navegacion = inject(NavegacionService);
      const cuentas = inject(CuentasService);
      return {
        tipo: 'cuenta',
        titulo: 'Cuentas',
        icono: 'user',
        minimo: 2,
        espera: 300,
        max: 8,
        activa: () => navegacion.puedeVer('cuentas'),
        buscar: (texto) =>
          cuentas
            .obtenerPagina({ busqueda: texto, page: 1, pageSize: 8 })
            .pipe(map((p) => p.items.map((c) => aResultadoCuenta(c)))),
        verTodos: (texto) => rutaDeCuentas(texto),
      };
    }),
  ];
}

/** Un catálogo como resultado: su nombre y, abajo, de dónde es y en qué tabla vive. */
export function aResultadoCatalogo(c: CatalogoResumen): IrAResultado {
  return {
    clave: c.clave,
    texto: c.plural,
    meta: [c.grupo === 'general' ? 'Catálogo general' : null, c.tabla].filter((x) => x).join(' · '),
    ruta: `/catalogos/${encodeURIComponent(c.clave)}`,
  };
}

/** El listado de cuentas filtrado por un texto: a donde llevan los resultados y «Ver todos». */
export function rutaDeCuentas(texto: string): string {
  return `/acceso/cuentas?buscar=${encodeURIComponent(texto)}`;
}

/**
 * Una cuenta como resultado: el nombre de la persona y, abajo, con qué entra
 * (`login`) y lo que la distingue —de servicio, dada de baja—. Lleva al listado
 * filtrado por su login, que es único.
 */
export function aResultadoCuenta(c: Cuenta): IrAResultado {
  const nombre = [c.nombre, c.primerApellido, c.segundoApellido].filter((x) => x).join(' ');
  return {
    clave: String(c.id),
    texto: nombre || c.login,
    meta: [c.login, c.esServicio ? 'De servicio' : null, c.activo ? null : 'De baja']
      .filter((x) => x)
      .join(' · '),
    ruta: rutaDeCuentas(c.login),
  };
}
